"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { useEffect, useState } from "react";
// internal
import menu_data from "../../../data/MenuData";
import { useLanguage } from "@/utils/LanguageContext";

interface MobileMenusProps {
    closeSidebar: () => void;
}

const MobileMenus = ({ closeSidebar }: MobileMenusProps) => {
    const [navTitle, setNavTitle] = useState("");
    const [activeHash, setActiveHash] = useState("");
    const currentRoute = usePathname();
    const { t, lang } = useLanguage();
    const homePath = `/${lang}/home`;

    useEffect(() => {
        const syncHash = () => setActiveHash(window.location.hash || "");
        syncHash();
        window.addEventListener("hashchange", syncHash);

        return () => window.removeEventListener("hashchange", syncHash);
    }, [currentRoute]);

    const buildLink = (link: string) => {
        if (link.startsWith('http')) return link;
        if (link === '/') return `/${lang}/home`;
        if (link.startsWith('/#')) return `/${lang}/home${link.substring(1)}`;
        return `/${lang}${link}`;
    };
    const shouldOpenInNewTab = (link: string) => link.startsWith("/flipbook/");

    const isHomeSectionLink = (link: string) => link.startsWith("/#");
    const extractHash = (link: string) => (isHomeSectionLink(link) ? link.slice(1) : "");

    const isMenuItemActive = (menuLink: string) => {
        if (isHomeSectionLink(menuLink)) {
            return currentRoute === homePath && activeHash === extractHash(menuLink);
        }

        if (menuLink === "/") {
            return currentRoute === homePath && activeHash === "";
        }

        return currentRoute === buildLink(menuLink);
    };

    const isSubMenuItemActive = (subMenuLink: string) => {
        if (isHomeSectionLink(subMenuLink)) {
            return currentRoute === homePath && activeHash === extractHash(subMenuLink);
        }

        return currentRoute === buildLink(subMenuLink);
    };

    //openMobileMenu
    const openMobileMenu = (menu: any) => {
        if (navTitle === menu) {
            setNavTitle("");
        } else {
            setNavTitle(menu);
        }
    };

    return (
        <ul className="navigation">
            {menu_data.filter((items) => items.page === "nav_1").map((menu) => (
                <React.Fragment key={menu.id}>
                    {menu.has_dropdown && (
                        <li className="menu-item-has-children">
                            <Link
                                href={buildLink(menu.link)}
                                prefetch={false}
                                onClick={closeSidebar}
                                target={shouldOpenInNewTab(menu.link) ? "_blank" : undefined}
                                rel={shouldOpenInNewTab(menu.link) ? "noopener noreferrer" : undefined}
                                className={`${(isMenuItemActive(menu.link) || (menu.sub_menus && menu.sub_menus.some((sub_m) => sub_m.link && isSubMenuItemActive(sub_m.link)))) ? "active" : ""}`}
                                aria-current={isMenuItemActive(menu.link) ? "page" : undefined}
                            >
                                {t(menu.title)}
                            </Link>
                            <button
                                type="button"
                                className={`dropdown-btn ${navTitle === menu.title ? "open" : ""}`}
                                onClick={() => openMobileMenu(menu.title)}
                                aria-expanded={navTitle === menu.title}
                                aria-controls={`mobile-submenu-${menu.id}`}
                                aria-label={t(menu.title)}
                            >
                                <i className={`${navTitle === menu.title ? "fas fa-angle-up" : "fas fa-angle-down"}`}></i>
                            </button>
                            {menu.sub_menus && menu.sub_menus.length > 0 && (
                                <ul
                                    id={`mobile-submenu-${menu.id}`}
                                    className="sub-menu"
                                    style={{ display: navTitle === menu.title ? "block" : "none" }}
                                >
                                    {menu.sub_menus.map((sub, index) => (
                                        <li key={index}>
                                            <Link
                                                href={buildLink(sub.link)}
                                                prefetch={false}
                                                onClick={closeSidebar}
                                                target={shouldOpenInNewTab(sub.link) ? "_blank" : undefined}
                                                rel={shouldOpenInNewTab(sub.link) ? "noopener noreferrer" : undefined}
                                                className={sub.link && isSubMenuItemActive(sub.link) ? "active" : ""}
                                                aria-current={sub.link && isSubMenuItemActive(sub.link) ? "page" : undefined}
                                            >
                                                {t(sub.title)}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    )}
                    {!menu.has_dropdown && (
                        <li>
                            <Link
                                onClick={closeSidebar}
                                href={buildLink(menu.link)}
                                prefetch={false}
                                target={shouldOpenInNewTab(menu.link) ? "_blank" : undefined}
                                rel={shouldOpenInNewTab(menu.link) ? "noopener noreferrer" : undefined}
                                className={`${isMenuItemActive(menu.link) ? "active" : ""}`}
                                aria-current={isMenuItemActive(menu.link) ? "page" : undefined}
                            >
                                {t(menu.title)}
                            </Link>
                        </li>
                    )}
                </React.Fragment>
            ))}
        </ul>
    );
}

export default MobileMenus;
